import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-actions/UserActionsAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  FormButton,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { PersonnelUsersFilter } from "./personnel-users-filter/PersonnelUsersFilter";
import { PersonnelUsersTable } from "./personnel-users-table/PersonnelUsersTable";
import { usePersonnelUsersUIContext } from "./PersonnelUsersUIContext";

export function PersonnelUsersCard() {
  // Personnel Users Card UI Context
  const personnelUsersUIContext = usePersonnelUsersUIContext();
  const personnelUsersUIProps = useMemo(() => {
    return {
      ids: personnelUsersUIContext.ids,
      setIds: personnelUsersUIContext.setIds,
      queryParams: personnelUsersUIContext.queryParams,
      openIPListEditModal: personnelUsersUIContext.openIPListEditModal
    };
  }, [personnelUsersUIContext]);

  // Getting curret state of UserActions from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userActions,
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isEditionAuthorized } = getAuthorities(authorities);
  
  // Personnel Users Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Personel Kullanıcı Listesini Excele Aktarır */
  const exportPersonnelUsersToExcel = () => {
    dispatch(actions.getAllPersonnelUsers(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 30, 30, 30, 15];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü", "Kullanıcı Grubu", "Aktif mi?"];
        
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.personnelType,
        entity.userGroups,
        entity.isActive ? "Aktif" : "Pasif"
      ]));
        
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı Listesi";
      const sheetname = "Personel Kullanıcı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Personel Kullanıcı Listesini PDF e Aktarır */
  const exportPersonnelUsersToPDF = () => {
    dispatch(actions.getAllPersonnelUsers(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Personel Türü",
          rows: entities.map(entity => entity.personnelType)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroups)
        },
        {
          header: "Aktif mi?",
          rows: entities.map(entity => entity.isActive ? "Aktif" : "Pasif")
        }
      ];
    
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı Listesi";
      exportToPDF(views, filename);
    });
  }
  
/** Belli Bir Personel Kullanıcı Listesini Yazdırma Penceresini Açar */
  const printPersonnelUsers = () => {
    dispatch(actions.getAllPersonnelUsers(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü", "Kullanıcı Grubu", "Aktif mi?"];
    
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Personel Türü": entity.personnelType,
          "Kullanıcı Grubu": entity.userGroups,
          "Aktif mi?": entity.isActive ? "Aktif" : "Pasif"
        });
      });
    
      print({ data, properties, header });
    });
  }
  
  /** Belli Bir Personel Kullanıcı Listesini Panoya Kopyalar */
  const copyPersonnelUsers = () => {
    dispatch(actions.getAllPersonnelUsers(personnelUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü", "Kullanıcı Grubu", "Aktif mi?"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Personel Kullanıcı Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.personnelType,
        entity.userGroups,
        entity.isActive ? "Aktif" : "Pasif"
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesinin Aktiflik Durumunu Değiştirir */
  const toggleActiveness = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
    
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
    dispatch(actions.toggleActiveness(ids)).then(result => onResult(result));
  }
  
  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
    
    if (isSuccess) {
      setTimeout(() => {
        personnelUsersUIProps.setIds([]);
        dispatch(actions.cleanUserActionsState());
        dispatch(actions.getPersonnelUsers(personnelUsersUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Personel Kullanıcı Listesi{personnelUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{personnelUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isEditionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportPersonnelUsersToExcel}
                exportToPDF={exportPersonnelUsersToPDF}
                print={printPersonnelUsers}
                copy={copyPersonnelUsers}
              />
            }
            {
              isEditionAuthorized &&
              <>
                <FormButton
                  path="Communication/Write"
                  title="IP Listesi Düzenle"
                  method={() => personnelUsersUIProps.openIPListEditModal(personnelUsersUIProps.ids)}
                />
                <DeleteButton
                  path="General/Settings-1"
                  title="Aktif Pasif Yap"
                  method={() => toggleActiveness(personnelUsersUIProps.ids)}
                />
              </>
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <PersonnelUsersFilter />
        <PersonnelUsersTable
          toggleActiveness={toggleActiveness}
        />
      </CardBody>
    </Card>
  );
}